/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type {
  Deal,
  DealConstraint,
  DealConstraintSkuDetails,
  SkuDetails,
} from '../../../interface';
import { useDealConstraintSkuDetails } from '../../../api/getDealConstraintSkuDetails';
import { useLabels } from '@/hooks/useLabels';
import cx from 'classnames';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@az/starc-ui';
import { LoadingIndicator } from '@/components/AZCustomComponent/LoadingIndicator';
import NotificationBlock from '@/components/AZCustomComponent/NotificationBlock/NotificationBlockV2';
import PaginationComp from '@/components/AZCustomComponent/Pagination/Pagination';
import { BundleDealConstraintCardV2 } from '../BundleDealConstraintCard/BundleDealConstraintCardV2';
import { NavBar } from '@/components/NavBar';
import styles from './ConstraintPicker.module.scss';
import { getErrorCodeDescription } from '@/utils/errorsHandling';

const chunk = (array: string[], chunk_size = 1) =>
  Array(Math.ceil(array.length / chunk_size))
    .fill(0)
    .map((_, index) => index * chunk_size)
    .map((begin) => array.slice(begin, begin + chunk_size));

type Props = {
  parentSkuDetails: SkuDetails;
  deal: Deal;
  constraint: DealConstraint;
  initialSelection: DealConstraintSkuDetails;
  skipInitialSelection?: boolean;
  allowDeselection?: boolean;
  onUpdate: (skuDetails: DealConstraintSkuDetails) => void;
  onBackClick: () => void;
  onClose: () => void;
  fulfillmentOption: string;
  isAutoOpenedBrakePadOrRotor?: boolean;
};

const labelMap = {
  label_deal_bundle_constraint_picker_update_item:
    'label_deal_bundle_constraint_picker_update_item',
  label_deal_bundle_constraint_picker_select_item:
    'label_deal_bundle_constraint_picker_select_item',
  label_deal_bundle_constraint_picker_pagination_label:
    'label_deal_bundle_constraint_picker_pagination_label',
  label_deal_bundle_unexpected_error: 'label_deal_bundle_unexpected_error',
  label_deal_bundle_constraint_picker_description_v2:
    'label_deal_bundle_constraint_picker_description_v2',
  label_deal_bundle_constraint_picker_title_v2: 'label_deal_bundle_constraint_picker_title_v2',
  label_deal_bundle_constraint_picker_select_title:
    'label_deal_bundle_constraint_picker_select_title',
};
const PAGE_SIZE = 5;

export const BundleDealConstraintPicker = ({
  parentSkuDetails,
  deal,
  constraint,
  initialSelection,
  skipInitialSelection,
  allowDeselection,
  onUpdate,
  onBackClick,
  onClose,
  fulfillmentOption,
  isAutoOpenedBrakePadOrRotor = false,
}: Props) => {
  const { id: skuId } = parentSkuDetails;
  const { id: dealId } = deal;
  const labels = useLabels(labelMap);

  const [currentSelection, setCurrentSelection] = useState<DealConstraintSkuDetails | null>(
    skipInitialSelection ? null : initialSelection,
  );
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const radioGroupRef = useRef<HTMLDivElement>(null);

  const [skuPages] = useState(() => chunk(constraint.skus, PAGE_SIZE));
  const currentPage = Math.floor(paginationOffset / PAGE_SIZE);
  const constraintSkusList = skuPages[currentPage] ?? [];
  const constraintSkuDetailsResult = useDealConstraintSkuDetails({
    dealId,
    parentSku: skuId,
    constraintSkusList,
    fulfillmentOption,
  });

  const skusHash = constraintSkuDetailsResult.data?.skus;
  const skuDetailsOptions = skusHash ? constraintSkusList.map((skuId) => skusHash[skuId]) : null;

  const onSelectionChange = (constraintSkuDetails: DealConstraintSkuDetails, index: number) => {
    if (allowDeselection && currentSelection?.id === constraintSkuDetails.id) {
      setCurrentSelection(null);
    } else {
      setCurrentSelection(constraintSkuDetails);
    }
    setFocusedIndex(index);
  };

  const onOffsetChange = (newOffset: number) => {
    setPaginationOffset(newOffset);
    setFocusedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!skuDetailsOptions) {
      return;
    }

    const maxIndex = skuDetailsOptions.length - 1;
    let newIndex = focusedIndex;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        newIndex = focusedIndex < maxIndex ? focusedIndex + 1 : 0;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = focusedIndex > 0 ? focusedIndex - 1 : maxIndex;
        break;
      default:
        return;
    }

    setFocusedIndex(newIndex);
    const radioButtons = radioGroupRef.current?.querySelectorAll('[role="radio"]');
    if (radioButtons?.[newIndex]) {
      (radioButtons[newIndex] as HTMLElement).focus();
    }
  };
  useEffect(() => {
    if (skuDetailsOptions && currentSelection) {
      const selectedIndex = skuDetailsOptions.findIndex((sku) => sku.id === currentSelection.id);
      if (selectedIndex !== -1 && selectedIndex !== focusedIndex) {
        setFocusedIndex(selectedIndex);
      }
    }

    // Omitting skuDetailsOptions and focusedIndex from dependencies to prevent interference with arrow key navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection?.id]);

  let content;

  if (constraintSkuDetailsResult.isError) {
    content = (
      <div className={styles.bundleDetailsBody}>
        <div className={styles.errorContainer}>
          <NotificationBlock
            type="error"
            message={
              getErrorCodeDescription(constraintSkuDetailsResult.error) ??
              labels.label_deal_bundle_unexpected_error
            }
          />
        </div>
      </div>
    );
  } else if (constraintSkuDetailsResult.isPending) {
    content = (
      <div className={styles.loadingContainer}>
        <LoadingIndicator />
      </div>
    );
  } else {
    content = (
      <div
        ref={radioGroupRef}
        role="radiogroup"
        aria-labelledby="constraint-picker-title"
        className={styles.content}
        onKeyDown={handleKeyDown}
      >
        {skuDetailsOptions?.map((constraintSkuDetails, index) => {
          const { id: skuId } = constraintSkuDetails;
          const selected = currentSelection?.id === skuId;
          const isCurrent =
            isAutoOpenedBrakePadOrRotor && skipInitialSelection
              ? false
              : initialSelection.id === skuId;
          const isFocused = index === focusedIndex;
          return (
            <BundleDealConstraintCardV2
              className={styles.constraintPickerCard}
              key={skuId}
              constraint={constraint}
              skuDetails={constraintSkuDetails}
              selected={selected}
              isCurrent={isCurrent}
              onClick={() => onSelectionChange(constraintSkuDetails, index)}
              tabIndex={isFocused ? 0 : -1}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.bundleConstraintPicker}>
      <NavBar
        showBackButton
        showCloseButton
        handleBack={onBackClick}
        handleClose={onClose}
        classes={{ mainContainer: styles.navBarContainer }}
      />
      <div className={styles.bundleConstraintBody}>
        <div className={styles.header}>
          <h2 className={styles.title} id="constraint-picker-title">
            {isAutoOpenedBrakePadOrRotor
              ? labels.label_deal_bundle_constraint_picker_select_title
              : labels.label_deal_bundle_constraint_picker_title_v2}
          </h2>
          <span className={styles.subtitle}>
            {labels.label_deal_bundle_constraint_picker_description_v2}
          </span>
        </div>

        <div className={cx(styles.separator, styles.noMargin)} />
        {content}
      </div>

      {!constraintSkuDetailsResult.isPending && (
        <>
          {skuPages.length > 1 && (
            <div className={styles.constraintPickerPagination}>
              <PaginationComp
                offset={paginationOffset}
                limit={PAGE_SIZE}
                total={constraint.skus.length}
                updateOffset={onOffsetChange}
                reduced={true}
              />
            </div>
          )}
        </>
      )}

      {constraintSkuDetailsResult.isSuccess && (
        <div className={styles.bundleDetailsFooter}>
          <Button
            onClick={() => currentSelection && onUpdate(currentSelection)}
            disabled={!currentSelection}
          >
            {(allowDeselection && !currentSelection) ||
            (isAutoOpenedBrakePadOrRotor && skipInitialSelection)
              ? labels.label_deal_bundle_constraint_picker_select_item
              : labels.label_deal_bundle_constraint_picker_update_item}
          </Button>
        </div>
      )}
    </div>
  );
};
